import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Input,
  Renderer2,
} from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ModalController } from '@ionic/angular';
import { EventItemComponent } from '../event-item/event-item.component';
import { ApiService } from 'src/app/services/api.service';
import { DialogUtility } from '@syncfusion/ej2-angular-popups';
import { Client } from 'src/app/models/client.model';
import { WasabiService } from 'src/app/services/wasabi.service';

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent implements OnInit, OnDestroy {
  @Input() iframeSrc = '';
  @Input() selectedFile?:any = null;
  @Input() addItemsData?: any;
  @Input() folderNodeId?: any;
  @Input() sectionType?: any;
  @Input() assetType?: string | number;
  @Input() client?: Client;
  @Input() assetSN?: string;
  /** Choose behaviour when Add Comment is clicked while doc viewer is fullscreen:
   *  'exit-fullscreen' -> restore doc viewer then open nested modal (default)
   *  'overlay'         -> keep doc viewer fullscreen and render nested modal above it
   */
  @Input() nestedModalBehavior: 'exit-fullscreen' | 'overlay' = 'overlay';

  // loading / embed state (your existing fields)
  isLoading = true;
  error: string | null = null;
  trustedUrl: any | null = null;
  fileExt: string | null = null;
  mimeType: string | null = null;
  objectUrl: string | null = null;
   excelHtml: string | null = null;
  mimeTypes: { [key: string]: string } = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg',
    };
  // resize & sizing
  minWidth = 1080;
  minHeight = 700;
  maxWidth = window.innerWidth - 40;
  maxHeight = window.innerHeight - 40;
  // track last non-fullscreen size so we can restore when exiting fullscreen
  lastWidth = 1080;
  lastHeight = 700;
  iframeHtml: SafeHtml;
  showAddEventItemForm:boolean = false
  showAddDocApprovalForm:boolean = false
  // topbar
  topBarHeight = 56;

  // fullscreen state
  isFullscreen = false;
  documentDownload = false;

  approval: string | null = null;
  approvalComment: string = '';

  // modal DOM references
  private modalContentEl: HTMLElement | null = null;

  // resize internals
  private resizing = false;
  // private resizeDir: 'right' | 'bottom' | 'corner' | null = null;
  private startX = 0;
  private startY = 0;
  private startW = 0;
  private startH = 0;

  // embed timeout fields (keep your previous values/constants)
  private embedLoadTimeout: any = null;
  private EMBED_TIMEOUT_MS = 20000;
  private resizeDir: string | null = null
  private startLeft = 0;
  private startTop = 0;
  // custom backdrop reference (when using overlay behavior)
  private customBackdropEl: HTMLElement | null = null;

  public documentDelete: boolean = false;

  public fileLocation: string = '';

   userRoleId = JSON.parse(localStorage.getItem('user'))?.roleId;

  constructor(
    private sanitizer: DomSanitizer,
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private modalController: ModalController,
    public apiService: ApiService,
    private wasabiService: WasabiService,
  ) {}

  ngOnInit() {
    if(!this.addItemsData){
        this.iframeHtml = this.sanitizer.bypassSecurityTrustHtml(this.iframeSrc);
    }
    // find modal content element (the visual wrapper)
    this.modalContentEl = this.findModalContentElement();
    if (!this.modalContentEl) {
      // try again shortly — ionic sometimes inserts after init
      setTimeout(() => {
        this.modalContentEl = this.findModalContentElement();
        if (this.modalContentEl) this.applyInitialSizing();
      }, 60);
    } else {
      this.applyInitialSizing();
    }

    // start loading doc if iframeSrc provided
    if (this.iframeSrc) this.prepare(this.iframeSrc);
    if(this.selectedFile){
      this.approval = this.selectedFile?.approvalStatus;
      this.approvalComment = this.selectedFile?.comment;
      this.getRoleViewData()
    }
    if (this.assetType === 1) {
      this.fileLocation = this.client?.clientID + '/MSN';
    } else {
      if (this.assetType === 2) {
        this.fileLocation = this.client?.clientID + '/ENG';
      } else {
        if (this.assetType === 3) {
          this.fileLocation = this.client?.clientID + '/APU';
        } else {
          if (this.assetType === 4) {
            this.fileLocation = this.client?.clientID + '/LG';
          } else {
            this.fileLocation = this.client?.clientID + '/PROP';
          }
        }
      }
    }

    // fullscreen change listener
    document.addEventListener('fullscreenchange', this.onFullscreenChangeBound);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChangeBound);
    document.addEventListener('mozfullscreenchange', this.onFullscreenChangeBound);
    document.addEventListener('MSFullscreenChange', this.onFullscreenChangeBound);
  }

  ngOnDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChangeBound);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChangeBound);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChangeBound);
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChangeBound);
    // cleanup object url
    if (this.objectUrl) {
      try { URL.revokeObjectURL(this.objectUrl); } catch {}
      this.objectUrl = null;
    }
    this.removeCustomBackdrop();
    this.clearEmbedTimeout();
  }

  // ---------------- sizing helpers ----------------
  private applyInitialSizing() {
    // If modal wrapper exists and no explicit size, apply defaults
    this.setModalSize(this.lastWidth, this.lastHeight);
    // set CSS var so inner calc can use topbar height
    this.modalContentEl!.style.setProperty('--top-bar-height', `${this.topBarHeight}px`);
  }

  private setModalSize(w: number, h: number) {
    // clamp
    const W = Math.max(this.minWidth, Math.min(this.maxWidth, Math.round(w)));
    const H = Math.max(this.minHeight, Math.min(this.maxHeight, Math.round(h)));
    this.lastWidth = W;
    this.lastHeight = H;
    // if modal element present, set inline styles to control its size
    if (this.modalContentEl) {
      // where to set size depends on how Ionic wraps the modal. We'll try a couple of targets:
      const targets: (HTMLElement | null)[] = [
        this.modalContentEl,
        this.modalContentEl.querySelector('.modal-wrapper') as HTMLElement | null,
        this.modalContentEl.querySelector('.modal-page') as HTMLElement | null
      ];
      for (const t of targets) {
        if (!t) continue;
        t.style.setProperty('width', `${W}px`, 'important');
        t.style.setProperty('height', `${H}px`, 'important');
        t.style.setProperty('max-width', '100%', 'important');
      }
    }
  }

  private restoreToLastSize() {
    this.setModalSize(this.lastWidth, this.lastHeight);
    // remove fullscreen fallback class if present
    if (this.modalContentEl) this.renderer.removeClass(this.modalContentEl, 'is-fullscreen');
    this.isFullscreen = false;
  }

  // ---------------- drag resize handlers ----------------
  // start resizing (called from template via (mousedown)/(touchstart))
    startResize(ev: MouseEvent | TouchEvent, dir: string) {
    ev.preventDefault();
    this.resizing = true;
    this.resizeDir = dir;

    const rect = this.modalContentEl?.getBoundingClientRect();
    if (!rect) return;

    this.startX = this.getClientX(ev);
    this.startY = this.getClientY(ev);
    this.startW = rect.width;
    this.startH = rect.height;
    this.startLeft = rect.left;
    this.startTop = rect.top;

    window.addEventListener('mousemove', this.onPointerMoveBound);
    window.addEventListener('mouseup', this.stopResizeBound);
    window.addEventListener('touchmove', this.onPointerMoveBound, { passive: false });
    window.addEventListener('touchend', this.stopResizeBound);
    document.body.style.userSelect = 'none';
  }

  private getClientX(e: MouseEvent | TouchEvent) {
    return (e as TouchEvent).touches ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
  }
  private getClientY(e: MouseEvent | TouchEvent) {
    return (e as TouchEvent).touches ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
  }

  private onPointerMoveBound = this.onPointerMove.bind(this);
  private stopResizeBound = this.stopResize.bind(this);

  private onPointerMove(e: MouseEvent | TouchEvent) {
    if (!this.resizing || !this.modalContentEl) return;
    e.preventDefault();

    const cx = this.getClientX(e);
    const cy = this.getClientY(e);
    const dx = cx - this.startX;
    const dy = cy - this.startY;

    let newW = this.startW;
    let newH = this.startH;
    let newLeft = this.startLeft;
    let newTop = this.startTop;

    if (this.resizeDir?.includes('right')) newW = this.startW + dx;
    if (this.resizeDir?.includes('left')) {
      newW = this.startW - dx;
      newLeft = this.startLeft + dx;
    }
    if (this.resizeDir?.includes('bottom')) newH = this.startH + dy;
    if (this.resizeDir?.includes('top')) {
      newH = this.startH - dy;
      newTop = this.startTop + dy;
    }

    newW = Math.max(this.minWidth, Math.min(this.maxWidth, newW));
    newH = Math.max(this.minHeight, Math.min(this.maxHeight, newH));
    newLeft = Math.min(Math.max(0, newLeft), window.innerWidth - newW);
    newTop = Math.min(Math.max(0, newTop), window.innerHeight - newH);

    this.modalContentEl.style.width = `${newW}px`;
    this.modalContentEl.style.height = `${newH}px`;
    this.modalContentEl.style.left = `${newLeft}px`;
    this.modalContentEl.style.top = `${newTop}px`;
  }

  private stopResize() {
    if (!this.resizing) return;
    this.resizing = false;
    this.resizeDir = null;
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', this.onPointerMoveBound);
    window.removeEventListener('mouseup', this.stopResizeBound);
    window.removeEventListener('touchmove', this.onPointerMoveBound);
    window.removeEventListener('touchend', this.stopResizeBound);
  }

  // ---------------- nested modal behaviour ----------------
//   async addItem() {
//     this.lastWidth = 1000;
//     this.lastHeight = 700;
//     try {
//         if ((document as any).exitFullscreen) await (document as any).exitFullscreen();
//         else if ((document as any).webkitExitFullscreen) await (document as any).webkitExitFullscreen();
//       } catch {}
//       this.restoreToLastSize();
//       setTimeout(async () => {
// // create and present nested modal (no Ionic backdrop)
//    const model = await this.modalController.create({
//     component: EventItemComponent,
//     componentProps: this.addItemsData,
//     backdropDismiss: false, // prevents closing when clicking outside
//     showBackdrop: false,    // we will manage our own backdrop
//     cssClass: 'nested-modal' // mark it so we can find the element
//   });



//   // short delay to let Ionic attach the modal element to the DOM
//   await new Promise(resolve => setTimeout(resolve, 20));

//   // locate the just-presented nested modal element
//   const modalEl = document.querySelector('ion-modal.nested-modal') as HTMLElement | null
//                || document.querySelector('.nested-modal') as HTMLElement | null;

//   // if we found it, insert backdrop right before it and push modal on top
//   if (modalEl) {
//     this.insertBackdropBeforeModal(modalEl);
//     this.bringModalAboveBackdrop(modalEl);
//   } else {
//     // fallback: create backdrop at top of body and attempt to bring modal front later
//     this.createSimpleBackdrop();
//     // try again later to bring modal front
//     setTimeout(() => {
//       const m = document.querySelector('ion-modal.nested-modal') as HTMLElement | null
//             || document.querySelector('.nested-modal') as HTMLElement | null;
//       if (m) this.bringModalAboveBackdrop(m);
//     }, 200);
//   }
//   const modalElement = model;

//     // 🔥 Style ONLY this modal
//     modalElement.style.setProperty('--width', '60%');
//     modalElement.style.setProperty('--height', '37%');
//     modalElement.style.setProperty('max-width', '100%');
//     modalElement.style.setProperty('max-height', '100%');
//     modalElement.style.setProperty('margin', 'auto');
//     modalElement.style.setProperty('display', 'flex');
//     modalElement.style.setProperty('flex-direction', 'column');
//     modalElement.style.setProperty('justify-content', 'center');
//     modalElement.style.setProperty('align-items', 'center');

//   await model.present();

//   // cleanup when nested modal closes
//   model.onDidDismiss().then(() => {
//     this.removeCustomBackdrop();
//   });
//         }, 100);

// }

/** Insert backdrop DOM node immediately before the nested modal element */
private insertBackdropBeforeModal(modalEl: HTMLElement) {
  // remove any existing custom backdrop
  this.removeCustomBackdrop();

  // compute highest z-index excluding the modalEl
  const baseZ = this.getHighestZIndexExcluding(modalEl);

  // backdrop will sit above existing overlays (baseZ+1), modal will be baseZ+2
  const backdropZ = baseZ + 1;
  const modalZ = baseZ + 2;

  // create backdrop element
  const backdrop = document.createElement('div');
  backdrop.id = 'custom-nested-backdrop';
  backdrop.className = 'custom-nested-backdrop';
  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.45)',
    backdropFilter: 'blur(2px)',
    zIndex: String(backdropZ),
    pointerEvents: 'auto'
  });

  // Insert before the modal element in DOM so stacking order is correct
  // (backdrop will be under the modal because it appears earlier in DOM)
  modalEl.parentNode?.insertBefore(backdrop, modalEl);

  // store for later removal
  (window as any).__customNestedBackdrop = backdrop;

  // prevent body scroll while nested modal open
  document.body.style.overflow = 'hidden';

  // make sure that modal and its inner wrapper have a higher z-index
  try {
    modalEl.style.setProperty('z-index', String(modalZ), 'important');
    const inner = modalEl.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement | null
               || modalEl.querySelector('.modal-wrapper') as HTMLElement | null;
    if (inner) inner.style.setProperty('z-index', String(modalZ), 'important');
  } catch (e) { /* ignore */ }

  // hide Ionic's own backdrop inside the nested modal (if present)
  try {
    const ionBackdrop = modalEl.shadowRoot?.querySelector('ion-backdrop') as HTMLElement | null
                     || modalEl.querySelector('ion-backdrop') as HTMLElement | null;
    if (ionBackdrop) ionBackdrop.style.setProperty('display', 'none', 'important');
  } catch (e) {}
}

/** Try to adjust z-index of nested modal after present */
private bringModalAboveBackdrop(modalEl: HTMLElement) {
  const backdrop = (window as any).__customNestedBackdrop as HTMLElement | null;
  if (!backdrop) {
    // fallback: create one at highest level
    this.insertBackdropBeforeModal(modalEl);
    return;
  }
  const backdropZ = parseInt(backdrop.style.zIndex || '10000', 10) || 10000;
  const modalZ = backdropZ + 1;
  try {
    modalEl.style.setProperty('z-index', String(modalZ), 'important');
    const inner = modalEl.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement | null
               || modalEl.querySelector('.modal-wrapper') as HTMLElement | null;
    if (inner) inner.style.setProperty('z-index', String(modalZ), 'important');
  } catch (e) { /* ignore */ }
}

/** Simple fallback backdrop if modal element couldn't be located */
private createSimpleBackdrop() {
  this.removeCustomBackdrop();
  const backdrop = document.createElement('div');
  backdrop.id = 'custom-nested-backdrop';
  backdrop.className = 'custom-nested-backdrop';
  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.45)',
    zIndex: String(10000),
    pointerEvents: 'auto'
  });
  document.body.appendChild(backdrop);
  document.body.style.overflow = 'hidden';
  (window as any).__customNestedBackdrop = backdrop;
}

/** Remove our custom backdrop and restore scroll */
private removeCustomBackdrop() {
  const d = document.getElementById('custom-nested-backdrop');
  if (d) d.remove();
  document.body.style.overflow = '';
  delete (window as any).__customNestedBackdrop;
}

/** Return the highest numeric z-index in the document excluding optional element */
private getHighestZIndexExcluding(excludeEl?: Element | null): number {
  const nodes = Array.from(document.querySelectorAll('body *')) as HTMLElement[];
  let max = 1000;
  for (const el of nodes) {
    if (!el.offsetParent) continue; // skip hidden elements
    if (excludeEl && (excludeEl === el || excludeEl.contains(el))) continue;
    const z = window.getComputedStyle(el).zIndex;
    const zi = parseInt(String(z), 10);
    if (!Number.isNaN(zi)) max = Math.max(max, zi);
  }
  return max;
}

  // ---------------- Fullscreen API ----------------
  private onFullscreenChangeBound = () => this.onFullscreenChange();
  private async enterFullscreenOn(el: HTMLElement) {
    try {
      const req = (el as any).requestFullscreen || (el as any).webkitRequestFullscreen || (el as any).mozRequestFullScreen || (el as any).msRequestFullscreen;
      if (req) {
        await req.call(el);
        this.renderer.addClass(el, 'is-fullscreen');
        this.isFullscreen = true;
        return true;
      }
    } catch (err) { console.warn('Fullscreen API failed', err); }
    return false;
  }

  async toggleFullScreen() {
    this.modalContentEl = this.modalContentEl || this.findModalContentElement();
    const targetEl = this.modalContentEl || this.elRef.nativeElement;
    if (!targetEl) return;

    const inFs = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
    if (inFs) {
      try {
        if ((document as any).exitFullscreen) await (document as any).exitFullscreen();
        else if ((document as any).webkitExitFullscreen) await (document as any).webkitExitFullscreen();
      } catch {}
      // remove fallback class and restore size
      if (this.modalContentEl) this.renderer.removeClass(this.modalContentEl, 'is-fullscreen');
      this.isFullscreen = false;
      this.restoreToLastSize();
      return;
    }

    // entering fullscreen
    // save current size
    this.lastWidth = this.lastWidth || 1000;
    this.lastHeight = this.lastHeight || 700;

    const ok = await this.enterFullscreenOn(targetEl);
    if (!ok) {
      // fallback: add css class
      if (this.modalContentEl) this.renderer.addClass(this.modalContentEl, 'is-fullscreen');
      this.isFullscreen = true;
      // make sure modal fills viewport via CSS rules
    }
  }

  private onFullscreenChange() {
    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).mozFullScreenElement || (document as any).msFullscreenElement;
    this.isFullscreen = !!fsEl;
    // if leaving fullscreen, restore sizes
    if (!this.isFullscreen) this.restoreToLastSize();
  }

  private findModalContentElement(): HTMLElement | null {
    const rootEl = document.querySelector('ion-modal.document-viewer') || document.querySelector('.document-viewer');
    if (!rootEl) return null;

    const tryShadow = (sel: string) => {
      try {
        if ((rootEl as any).shadowRoot) {
          return (rootEl as any).shadowRoot.querySelector(sel) as HTMLElement | null;
        }
      } catch {}
      return null;
    };

    const selectors = ['.modal-wrapper', '.modal-page', '.modal-content', '.modal', '.modal-host', '.ion-page'];
    for (const sel of selectors) {
      const found = tryShadow(sel) || (rootEl.querySelector ? rootEl.querySelector(sel) : null);
      if (found) return found as HTMLElement;
    }

    const firstChild = rootEl.firstElementChild as HTMLElement | null;
    if (firstChild) return firstChild;
    return null;
  }


  private cleanup() {
    if (this.objectUrl) {
      try { URL.revokeObjectURL(this.objectUrl); } catch {}
      this.objectUrl = null;
    }
  }

  restrictAddItemToJustfiles(){
    return ['doc','docx','xls','xlsx','ppt','pptx','pdf'].includes(this.fileExt)
  }


  // ---------- PREPARE / EMBED LOGIC (keep as you had it; simplified here) ----------
  // Updated prepare() — only differences: never set isLoading = false immediately
private async prepare(url: string) {
  console.log('prepare runs for', url);
  this.cleanup();
  this.trustedUrl = null;
  this.excelHtml = null;
  this.error = null;
  // DO NOT set isLoading = false here — keep as true until embed load
  this.isLoading = true;

  if (!url) {
    this.isLoading = false;
    return;
  }

  try {
    const pathname = new URL(url).pathname;
    this.fileExt = pathname.split('.').pop()?.toLowerCase() || null;
  } catch {
    const q = url.indexOf('?') === -1 ? url : url.split('?')[0];
    this.fileExt = q.split('.').pop()?.toLowerCase() || null;
  }

  this.mimeType = this.mimeTypes[this.fileExt || ''] || null;

  // OFFICE (embed via Office viewer) — start timeout and wait for iframe load
  if (this.fileExt && ['doc','docx','xls','xlsx','ppt','pptx'].includes(this.fileExt)) {
    try {
      const officeViewer = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(officeViewer);
      this.startEmbedTimeout();
    } catch (err) {
      console.warn('Office viewer prepare error:', err);
      this.error = 'Unable to embed this Office document.';
      this.isLoading = false;
    }
    return;
  }

  // PDF: fetch blob then start waiting for object/iframe load
  if (this.fileExt === 'pdf') {
    try {
      // const resp = await fetch(url);
      // if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // const blob = await resp.blob();
      // // this.objectUrl = URL.createObjectURL(blob);
      // this.trustedUrl = blob;
      this.trustedUrl = url;
      // this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
       this.clearEmbedTimeout();
       setTimeout(() => {
         this.isLoading = false
       },8000)
      // this.startEmbedTimeout();
    } catch (err) {
      console.error('Fetch failed for PDF:', err);
      this.error = `Failed to fetch PDF file. Possibly CORS issue or restricted URL.`;
      this.isLoading = false;
    }
    return;
  }

  // IMAGES — set URL and wait for image load event
  if (['jpg','jpeg','png','gif','bmp','webp','svg'].includes(this.fileExt || '')) {
    this.trustedUrl = url;
    // this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.startEmbedTimeout();
    return;
  }

  // VIDEOS — set url and wait for loadedmetadata/loadeddata
  if (['mp4','webm','ogg'].includes(this.fileExt || '')) {
    this.trustedUrl = url;
    // this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.startEmbedTimeout();
    return;
  }

  // TEXT files — fetch, create blob and wait for iframe load
  if (['txt','log','md'].includes(this.fileExt || '')) {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const txt = await resp.text();
      const html = `<!doctype html><html><body><pre>${this.escapeHtml(txt)}</pre></body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      this.objectUrl = URL.createObjectURL(blob);
      this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
      this.startEmbedTimeout();
    } catch (err) {
      console.error('Text fetch failed:', err);
      this.error = 'Failed to load text file.';
      this.isLoading = false;
    }
    return;
  }

  // Fallback: try direct embed and wait for load
  this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  this.startEmbedTimeout();
}


  // Called when embedded element dispatches load / loadedmetadata / loadeddata events
  onEmbedLoaded() {
  // Stop the timeout and hide loader after a tiny delay so content is visually ready
  this.clearEmbedTimeout();
  setTimeout(() => {
    this.isLoading = false;
  }, 120);
}

private startEmbedTimeout() {
  // Keep loader visible until embed signals load or timeout fires
  this.clearEmbedTimeout();
  this.embedLoadTimeout = setTimeout(() => {
    if (this.isLoading) {
      this.isLoading = false;
      this.error = 'Preview timed out. Document may be blocked by CORS or not publicly accessible.';
      console.warn('Embed timed out.');
    }
  }, this.EMBED_TIMEOUT_MS);
}

private clearEmbedTimeout() {
  if (this.embedLoadTimeout) {
    clearTimeout(this.embedLoadTimeout);
    this.embedLoadTimeout = null;
  }
}
private escapeHtml(s: string) {
    return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }
  closeModal() {
    // dismiss the Ionic modal
    this.modalController.dismiss({
        'dismissed': false
      });
  }

  openOrDownloadFile(fileUrl: string) {
    console.log(fileUrl)
    this.documentDownload = true;
    // Extract and process the filename
    let fileName = decodeURI(fileUrl.split('/').pop() || 'download');
    // fileName = this.getProcessedFileName(fileName); // Apply filename processing

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName; // Use the processed filename

    // Attempt to download the file using fetch if it's a cross-origin URL
    fetch(fileUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error('File download failed');
        }
        return response.blob();
      })
      .then((blob) => {
        // Create a Blob URL and trigger the download
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.click();
        // Clean up the Blob URL after download
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error downloading file:', error);
        // Fallback to opening the file directly if the download fails
        window.open(fileUrl, '_blank');
      })
      .finally(() => {
        this.documentDownload = false;
      });
  }

  onApprovalChange(event: any) {
    this.approval = event.value;
    this.approvalComment = '';
  }

  onApprovalCommentChange(event: any) {
    this.approvalComment = event.target.value;
  }

  async saveItem() {

    if (this.addItemsData?.eventId) {
      this.apiService.updateOilEventDocumentStatus({
        eventId: this.addItemsData?.eventId,
        assetId: this.addItemsData?.assetObj.assetId,
        assetTypeId: this.addItemsData?.assetObj.assetTypeId,
        assetDocId: this.addItemsData?.documentId,
        approvalStatus: this.approval,
        comment: this.approvalComment
      }).subscribe((res: any) => {
        this.apiService.openSnackBar('Data Updated Successfully', null)
         this.modalController.dismiss({
        'dismissed': true
      });
      }, (err: any) => {
         this.modalController.dismiss({
        'dismissed': false
      });
        this.apiService.openErrorSnackBar(err?.error?.error ? err?.error?.error : 'Please contact support team', null)
      }), (err: any) => {
         this.modalController.dismiss({
        'dismissed': false
      });
        this.apiService.openErrorSnackBar(err?.error?.error ? err?.error?.error : 'Please contact support team', null)
      };
    }
  }

   async viewItem() {
    try {
      (this.apiService.getOilEventItemDataById(this.addItemsData?.eventId,this.addItemsData?.itemId).subscribe(
        (res: any) => {
          if(res?.data?.length>0){
            this.openViewItemModel(res?.data[0])
          }
        },
        (err) => {
          this.apiService.openErrorSnackBar(
            err?.error?.error ? err?.error?.error : 'Please contact support team',
            null
          );
        }
      )),
        (err) => {
          this.apiService.openErrorSnackBar(
            err?.error?.error ? err?.error?.error : 'Please contact support team',
            null
          );
        };
    } catch (err) {
      this.apiService.openErrorSnackBar(err?.error?.error ? err?.error?.error : 'Please contact support team', null);
    }

    }

    async openViewItemModel(item){
       const modal = await this.modalController.create({
        component: EventItemComponent,
        componentProps: {
          type: 'View',
          item,
          eventId: this.addItemsData?.eventId,
          assetObj: { assetTypeId: this.addItemsData?.assetObj?.assetTypeId, assetId: this.addItemsData?.assetObj?.assetId },
        },
        backdropDismiss: true,
        cssClass: 'item-view-from-event-category',
      });

      modal.onDidDismiss().then(() => {
        modal?.dismiss();
      });

      await modal.present();
    }

  // ---------------- existing embed / loader utilities --------------
  // ... paste your existing prepare(), startEmbedTimeout(), onEmbedLoaded(), clearEmbedTimeout(), escapeHtml() here ...
  // make sure in your prepare() you do not set isLoading = false immediately after assigning trustedUrl,
  // but instead call this.startEmbedTimeout() and rely on onEmbedLoaded() to set isLoading = false.

  // For brevity in this snippet we won't duplicate prepare() — use the version you already have that uses startEmbedTimeout()/onEmbedLoaded().

  public DialogObj;

    public onOpenDialog = function (): void {
      // const filename: string[] = this.grid.getSelectedRecords().map((e) => e['title']);
      const filename = this.selectedFile.fileName;
      this.DialogObj = DialogUtility.confirm({
        title: 'Delete Confirmation',
        content: 'Delete document ' + filename + ' ?',
        okButton: { text: 'OK', click: this.okClick.bind(this) },
        cancelButton: { text: 'Cancel', click: this.cancelClick.bind(this) },
        showCloseIcon: true,
        closeOnEscape: true,
        animationSettings: { effect: 'Zoom' },
      });
    };

  private okClick(): void {
    this.documentDelete = true;
    this.deleteDocument();
    this.DialogObj.hide();
  }

  private cancelClick(): void {
    //Hide the dialog
    this.DialogObj.hide();
  }
  public deleteDocument(): void {
    const id: string[] = this.selectedFile.id;
    const filename: string[] = this.selectedFile.fileName;
    const dtd = {
      clientID: JSON.parse(localStorage.getItem('client'))?.clientID,
      section: this.sectionType,
      recordId: id,
    };
    try {
      // switch (this.assetType) {
      //   case 1:
      (this.apiService.deleteDoc(dtd).subscribe(
        (data) => {
          this.apiService.openSnackBar('Successfully Deleted', null);
          this.wasabiService.deleteFile(this.fileLocation + '/' + this.assetSN, filename[0]);
          this.DialogObj.hide();
          this.documentDelete = false;
           this.modalController.dismiss({
        'dismissed': true
      });
        },
        (err) => {
          this.documentDelete = false;
          this.apiService.openErrorSnackBar(
            err?.error?.error ? err?.error?.error : 'Please contact support team',
            null
          );
        }
      )),
        (err) => {
          this.documentDelete = false;
          this.apiService.openErrorSnackBar(
            err?.error?.error ? err?.error?.error : 'Please contact support team',
            null
          );
        };
    } catch (err) {
      this.documentDelete = false;
      this.apiService.openErrorSnackBar(err?.error?.error ? err?.error?.error : 'Please contact support team', null);
    }

  }

  hasAddPermission(valueCheck: any): boolean {
    if (this.userRole) {
      const resource = this.userRole?.formatted_permissions.find((p) => p.resource_name === 'Assets');
      return valueCheck === 'add' ? !!resource?.can_add : !resource?.can_delete;
    }
  }
   userRole: any
  getRoleViewData() {
    this.apiService.getRoleById(this.userRoleId).subscribe(
      (data) => {
        this.userRole = data
      },
      (err) => {
        this.apiService.openErrorSnackBar(
          err?.error?.error ? err?.error?.error : 'Please contact support team',
          null
        );
      })
  }
}
